import { computed, type Ref } from "vue";
import { useDSFileUploadFieldNames } from "./useDSFileUploadFieldNames";
import { useDSFileUploadInputSync } from "./useDSFileUploadInputSync";
import { useDSFileUploadInteractions } from "./useDSFileUploadInteractions";
import { useDSFileUploadModel } from "./useDSFileUploadModel";
import { useDSFileUploadPresentation } from "./useDSFileUploadPresentation";
import { useDSFileUploadValidation } from "./useDSFileUploadValidation";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import type {
  DSFileUploadComponentProps,
  DSFileUploadEmits,
  DSFileUploadItem,
  DSFileUploadModelValue,
  DSFileUploadRejectedFile,
} from "../types";

interface UseDSFileUploadOptions {
  rootRef: Ref<HTMLElement | undefined>;
  props: DSFileUploadComponentProps;
  emit: DSFileUploadEmits;
}

export function useDSFileUpload({ props, emit, rootRef }: UseDSFileUploadOptions) {
  const { id } = useDSFormFieldId({ props });
  const {
    addAcceptedFiles,
    modelValue,
    removeAllFiles: removeAllModelFiles,
    removeFile: removeModelFile,
  } = useDSFileUploadModel({ props, emit });

  const { fileFieldName, filenameFieldName } = useDSFileUploadFieldNames({ props, id });

  const dsFormField = useDSFormField<DSFileUploadModelValue>({
    name: computed(() => props.name),
    errorMessages: computed(() => props.errorMessages),
    value: modelValue,
    rules: computed(() => props.rules),
  });

  const { acceptAttribute, validateSelectedFiles } = useDSFileUploadValidation({ props, modelValue });

  const {
    blur,
    dropzoneRef,
    focus,
    isDragActive,
    isDropzoneFocused,
    isPageDragActive,
    onBlur,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    onFocus,
    openFileDialog,
    resetInput,
    setInputFiles,
  } = useDSFileUploadInteractions({ props, emit, onFilesSelected, rootRef });

  const { syncInputFiles } = useDSFileUploadInputSync({ props, modelValue, setInputFiles });

  function onFilesSelected(files: File[], event: Event | DragEvent): void {
    if (props.disabled) return;
    if (!files.length) return;

    const { accepted, rejected } = validateSelectedFiles(files);
    emitRejectedFiles(rejected);
    const nextModelValue = addAcceptedFiles(accepted, event) ?? modelValue.value;
    syncInputFiles(nextModelValue);
    if (accepted.length) {
      dsFormField.clearFieldErrors();
    }
  }

  function emitRejectedFiles(rejected: DSFileUploadRejectedFile[]): void {
    if (rejected.length) emit("reject", rejected);
  }

  function removeFile(item: DSFileUploadItem): void {
    const nextModelValue = removeModelFile(item);
    if (nextModelValue) syncInputFiles(nextModelValue);
  }

  function removeAllFiles(): void {
    const nextModelValue = removeAllModelFiles();
    if (nextModelValue) syncInputFiles(nextModelValue);
  }

  const { hasError, rootClasses, showFiles, showProgressBar } = useDSFileUploadPresentation({
    props,
    modelValue,
    fieldErrorMessages: dsFormField.errorMessages,
    isDragActive,
    isDropzoneFocused,
  });

  return {
    acceptAttribute,
    blur,
    dsFormField,
    dropzoneRef,
    fileFieldName,
    filenameFieldName,
    focus,
    hasError,
    id,
    isDropzoneFocused,
    isPageDragActive,
    modelValue,
    onBlur,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    onFilesSelected,
    onFocus,
    openFileDialog,
    removeAllFiles,
    removeFile,
    resetInput,
    rootClasses,
    showFiles,
    showProgressBar,
  };
}
